/* eslint-disable react-hooks/exhaustive-deps */
import React, {useReducer, useEffect, useMemo} from "react"
import {useTranslation} from "react-i18next"
import {useEdularModulesContext, Modules} from "@edular/modules"
import {ApplicationProgress, Checklist} from "sections/shared/student"
import {handleError} from "helpers"
import {studentService} from "services"
import {useActiveTab, useCurrentProfilePermissions, useModel} from "hooks"
import {Spin, Tabs} from "antd"
import ActivitiesTable from "sections/shared/student/ActivitiesTable/ActivitiesTable"
import {Model} from "Model"
import WorkflowTriggersTable from "sections/Workflow/WorkflowTriggersTable"
import CalendarEventsTable from "sections/shared/student/CalendarEventsTable"
import {Permissions} from "@edular/permissions"
import {TabHeader} from "components/Tab"
import {StudentNoteTab} from "sections/NewCommunication/shared"
import {useQuery} from "@tanstack/react-query"

const {TabPane} = Tabs

function reducer(state, action) {
    return {...state, ...action}
}

export enum AdmissionStudentAdmissionSubtab {
    Activities = "activities",
    WorkflowTriggers = "workflow-triggers",
    CalendarEvents = "calendar-events",
    Checklist = "checklist"
}

type Props = {
    studentId: number
    departmentId: number
    model: Model
}

export function AdmissionStudentTab(props: Props) {
    const {studentId, departmentId} = props
    const {t} = useTranslation(["user", "common"])

    const {isModuleEnable} = useEdularModulesContext()
    const model = useModel()
    const canShowActivity = isModuleEnable(Modules.ActivitiesAndFollowups)
    const currentProfilePermissions = useCurrentProfilePermissions()
    const canShowWorkflowTriggers = useMemo(
        () =>
            currentProfilePermissions.hasPermissions({
                staff: [Permissions.Staff.Settings.General.Workflow.Add]
            }),
        [currentProfilePermissions]
    )
    const [activeTabKey, onChangeTab] = useActiveTab(
        canShowActivity ? AdmissionStudentAdmissionSubtab.Activities : AdmissionStudentAdmissionSubtab.Checklist,
        "subtab"
    )

    const {data: student, isLoading} = useQuery({
        queryKey: ["studentDetail", studentId, departmentId],
        queryFn: async () => {
            const {
                data: [student]
            } = await studentService.getDepartmentStudents({
                filter: {profileIds: [studentId], departmentId}
            })
            return student
        }
    })

    return (
        <div>
            <ApplicationProgress departmentId={props.departmentId} studentId={props.studentId} />
            <Tabs className="fullwidth klassSubTab" activeKey={activeTabKey} onChange={onChangeTab}>
                {canShowActivity && (
                    <TabPane
                        tab={<TabHeader title={t("user.activities")} />}
                        key={AdmissionStudentAdmissionSubtab.Activities}>
                        <ActivitiesTable studentId={studentId} departmentId={departmentId} />
                    </TabPane>
                )}
                {canShowWorkflowTriggers && (
                    <TabPane
                        tab={<TabHeader title={t("user.workflowTriggers")} />}
                        key={AdmissionStudentAdmissionSubtab.WorkflowTriggers}>
                        <WorkflowTriggersTable profileId={studentId} departmentId={departmentId} />
                    </TabPane>
                )}
                {canShowWorkflowTriggers && (
                    <TabPane
                        tab={<TabHeader title={t("user.calendarEvents")} />}
                        key={AdmissionStudentAdmissionSubtab.CalendarEvents}>
                        {isLoading ? (
                            <Spin />
                        ) : (
                            <CalendarEventsTable
                                studentId={studentId}
                                departmentId={departmentId}
                                studentUserId={student.userId}
                            />
                        )}
                    </TabPane>
                )}
                <TabPane
                    tab={<TabHeader title={t("user.checklist")} />}
                    key={AdmissionStudentAdmissionSubtab.Checklist}>
                    <Checklist studentId={studentId} departmentId={departmentId} />
                </TabPane>
            </Tabs>
        </div>
    )
}
